'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Loader2, Settings2, ShieldCheck } from 'lucide-react'
import { BaseDashboard, baseDashboardClasses } from '../BaseDashboard'
import { ResizableTable, TableTd, type ColumnDef } from '../ResizableTable'
import { StatTile } from '../charts/StatTile'
import { EmailPillInput } from '../EmailPillInput'
import { apiGet, apiPost, useCatalog } from './entriesApi'
import { Chip } from './autoDomainChips'
import { SearchableSelect } from './SearchableSelect'
import { ActionsWidget } from '../EntryDetail/ActionsWidget'
import { type ReportChannel, type EntryActionFlat, type ReportTaskStatus, TASK_STATUS_LABEL, TASK_STATUS_TONE, MANUAL_CHANNELS, isManualChannel } from './actionTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { parseDbTimestamp } from '@/lib/dates'

// Administration › Actions — every action taken across brands (entry_action,
// fraud-dbs 20260915000001; jupyter-fraud libs/tooling/mitigation): the
// planned takedown reports and the manual sends an operator fired from their
// own screen (the SIBS alert, the SIBS case report, a composed email, a
// domain marked inactive). Plus the two things an operator configures here:
// per-brand channel settings (the CNCS address, whether a channel is on, the
// reporter identity the forms use) and the registrar abuse contacts the
// WHOIS does not give. Editor only.
// A row expands into the entry's own Actions widget, so every button an
// analyst has on the entry is here too.

type Summary = { planned: number; queued: number; awaiting_browser: number; sent: number; unanswered_48h: number; takedown_hints: number; failed: number; resolved_7d: number; manual_sends_7d: number }
type ActionList = { rows: EntryActionFlat[]; total: number; page: number; pageSize: number }
type ChannelConfigRow = { id: string; platform_id: number | null; channel: string; enabled: boolean; recipients: string[]; params: Record<string, any>; updated_by: string | null; updated_at: string }
type RegistrarRow = { id: string; registrar_pattern: string; abuse_email: string | null; form_url: string | null; override_whois: boolean; priority: number; notes: string | null }
type ConfigPayload = { channels: ChannelConfigRow[]; registrars: RegistrarRow[]; catalog: ReportChannel[] }

const STATUS_ORDER: ReportTaskStatus[] = ['planned', 'queued', 'sending', 'awaiting_browser', 'sent', 'acknowledged', 'failed', 'resolved', 'skipped', 'cancelled']
const PLANNED_CHANNELS = ['registrar_abuse', 'cncs', 'google_safe_browsing', 'cloudflare_abuse', 'microsoft_report', 'generic_form']

function fmtDate(iso?: string | null) {
  const d = parseDbTimestamp(iso ?? undefined)
  if (!d) return '—'
  const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
  const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit' }).format(d)
  return `${time} ${date}`
}

function fmtHours(h?: number | null) {
  if (h == null) return '—'
  if (h < 1) return `${Math.round(h * 60)}m`
  if (h < 48) return `${Math.round(h)}h`
  return `${Math.round(h / 24)}d`
}

// What an operator should do next with a row, in one phrase.
function nextAction(t: EntryActionFlat): string {
  switch (t.status) {
    case 'planned': return 'press Report'
    case 'queued': return 'not sent — Retry or the backstop'
    case 'sending': return 'sending now'
    case 'awaiting_browser': return t.handoff_claimed_by ? `browser pane ${t.handoff_claimed_by} on it` : 'needs the browser pane'
    case 'sent': return (t.hours_since_sent ?? 0) > 48 ? 'no reply in 48 h — chase or resolve' : 'waiting for a reply'
    case 'acknowledged': return t.resolution_candidate ? 'reply says taken down — confirm and resolve' : 'read the reply, resolve'
    case 'failed': return `retry (${t.last_error?.split('\n')[0] ?? 'failed'})`
    case 'resolved': return t.resolution ?? 'resolved'
    case 'skipped': return t.applicability?.reason ?? 'not applicable'
    default: return '—'
  }
}

export function ActionsView({ theme }: { theme: ProcessedTheme }) {
  const { isDark, muted, border, panelBg, text } = baseDashboardClasses(theme)
  const { catalog } = useCatalog()
  const platformName = useCallback((id: number | null) => (id == null ? null : catalog.platforms.find((p) => p.id === id)?.name_code ?? `#${id}`), [catalog])

  const [summary, setSummary] = useState<Summary | null>(null)
  const [list, setList] = useState<ActionList | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [statuses, setStatuses] = useState<ReportTaskStatus[]>([])
  const [channels, setChannels] = useState<string[]>([])
  const [platformId, setPlatformId] = useState<number | ''>('')
  const [q, setQ] = useState('')
  const [openOnly, setOpenOnly] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return new URLSearchParams(window.location.search).get('action')
  })
  const [showConfig, setShowConfig] = useState(false)
  const [config, setConfig] = useState<ConfigPayload | null>(null)
  const [configNote, setConfigNote] = useState<string | null>(null)

  const fetchList = useCallback(async (page = 0) => {
    const p = new URLSearchParams({ page: String(page) })
    if (statuses.length) p.set('status', statuses.join(','))
    else if (openOnly) p.set('open', '1')
    if (channels.length) p.set('channel', channels.join(','))
    if (platformId !== '') p.set('platform_ids', String(platformId))
    if (q.trim()) p.set('q', q.trim())
    const r = await apiGet<ActionList>(`/actions/list?${p}`)
    if (r.success && r.data) { setList(page === 0 ? r.data : { ...r.data, rows: [...(list?.rows ?? []), ...r.data.rows] }); setError(null) }
    else setError(r.error ?? 'Could not load the actions')
  }, [statuses, channels, platformId, q, openOnly, list?.rows])

  const fetchSummary = useCallback(async () => {
    const r = await apiGet<Summary>('/actions/summary')
    if (r.success && r.data) setSummary(r.data)
  }, [])

  const refreshAll = useCallback(async () => {
    setRefreshing(true)
    await Promise.all([fetchSummary(), fetchList(0)])
    setRefreshing(false); setLoading(false); setLastUpdated(Date.now())
  }, [fetchSummary, fetchList])

  useEffect(() => { setLoading(true); refreshAll() }, [statuses, channels, platformId, openOnly]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = setInterval(() => { fetchSummary(); fetchList(0) }, 30000)
    return () => clearInterval(id)
  }, [fetchSummary, fetchList])

  const loadConfig = useCallback(async () => {
    const r = await apiGet<ConfigPayload>('/actions/config')
    if (r.success && r.data) setConfig(r.data)
    else setConfigNote(r.error ?? 'Could not load the configuration')
  }, [])
  useEffect(() => { if (showConfig && !config) loadConfig() }, [showConfig, config, loadConfig])

  const saveChannel = async (row: { platform_id: number | null; channel: string; enabled?: boolean; recipients?: string[]; params?: Record<string, any> }) => {
    const r = await apiPost('/actions/config', row)
    setConfigNote(r.success ? 'Saved — the next plan uses it.' : (r.error ?? 'Could not save'))
    if (r.success) loadConfig()
  }
  const saveRegistrar = async (row: Partial<RegistrarRow>) => {
    const r = await apiPost('/actions/registrars', row)
    setConfigNote(r.success ? 'Saved.' : (r.error ?? 'Could not save'))
    if (r.success) loadConfig()
  }
  const deleteRegistrar = async (row: RegistrarRow) => {
    const r = await apiPost(`/actions/registrars/${row.id}/delete`, {})
    setConfigNote(r.success ? 'Deleted.' : (r.error ?? 'Could not delete'))
    if (r.success) loadConfig()
  }

  const tile = (label: string, value: number | undefined, tone: 'red' | 'amber' | 'sky' | 'green') => (
    <div className={`col-span-1 overflow-hidden border ${border}`}>
      <StatTile label={label} value={value == null ? '—' : String(value)} tone={tone} isDark={isDark} />
    </div>
  )

  const columns: ColumnDef[] = [
    { key: 'x', label: '', initialWidth: 28 },
    { key: 'brand', label: 'Brand', initialWidth: 80 },
    { key: 'hostname', label: 'Domain', initialWidth: 220 },
    { key: 'channel', label: 'Channel', initialWidth: 150 },
    { key: 'status', label: 'Status', initialWidth: 120 },
    { key: 'target', label: 'Target', initialWidth: 220 },
    { key: 'age', label: 'Age', initialWidth: 60 },
    { key: 'sent', label: 'Sent', initialWidth: 100 },
    { key: 'reply', label: 'Last reply', initialWidth: 260 },
    { key: 'next', label: 'Next', initialWidth: 260 },
  ]
  // The chips read in two groups: what the planner files, and what an
  // operator did somewhere else and the platform only logged.
  const catalogChannels = useMemo(() => config?.catalog ?? [], [config])
  const plannedChannelIds = useMemo(() => (catalogChannels.length ? catalogChannels.filter((c) => !c.manual_only).map((c) => c.id) : PLANNED_CHANNELS), [catalogChannels])
  const manualChannelIds = useMemo(() => (catalogChannels.length ? catalogChannels.filter((c) => c.manual_only).map((c) => c.id) : [...MANUAL_CHANNELS, 'custom_email']), [catalogChannels])
  const rows = list?.rows ?? []
  const hasMore = list ? (list.page + 1) * list.pageSize < list.total : false

  const channelChip = (ch: string) => {
    const active = channels.includes(ch)
    return (
      <button key={ch} type="button" onClick={() => setChannels((prev) => (prev.includes(ch) ? prev.filter((x) => x !== ch) : [...prev, ch]))}
        className={`rounded-[4px] border px-2.5 py-0.5 font-mono text-[10px] tracking-widest transition-colors ${active ? 'border-success bg-success-surface text-success' : `${border} ${muted} hover:text-success hover:border-success`}`}>
        {ch}
      </button>
    )
  }

  return (
    <BaseDashboard
      theme={theme}
      eyebrow="Operations · Actions"
      title="Actions taken"
      subtitle="Every action taken across brands — the takedown reports the planner files and the manual sends an operator fired — and the channels' configuration."
      infoContent={
        <>
          <p>When a domain is promoted (by the analysis or by hand) the planner records, per channel, whether a report applies and to whom — the registrar's abuse desk from WHOIS, the brand's CNCS address, the browser forms. <strong>Nothing is sent by itself.</strong> Report sends the email in the same request (through Resend, tagged so the reply lands on the row); a browser form is parked for the laptop's Chrome pane, which marks the row sent the moment it answers.</p>
          <p className="mt-1.5">The manual channels — the SIBS alert, the SIBS case report, a composed email, a domain marked inactive — are never planned: each is logged when an operator sends it from the screen that owns it, and appears here as the record of what was done.</p>
          <p className="mt-1.5">Replies to an emailed report come back to its row (the <span className="font-mono">[REF-…]</span> tag), flagged when they read like a takedown. Resolving is always a person's call.</p>
        </>
      }
      onRefresh={refreshAll}
      refreshing={refreshing}
      lastUpdated={lastUpdated}
      storageKey="actions-header"
      widgets={
        <>
          {tile('Ready to send', summary?.planned, 'sky')}
          {tile('Sending · queued', summary?.queued, 'amber')}
          {tile('Awaiting browser', summary?.awaiting_browser, 'amber')}
          {tile('Sent · no reply 48h', summary?.unanswered_48h, 'amber')}
          {tile('Reply says taken down', summary?.takedown_hints, 'green')}
          {tile('Failed', summary?.failed, 'red')}
          {tile('Resolved · 7d', summary?.resolved_7d, 'green')}
          {tile('Manual sends · 7d', summary?.manual_sends_7d, 'sky')}
        </>
      }
      widgetCols={8}
      widgetRows="5.25rem"
    >
      {error && <div className={`mx-4 mt-3 rounded border px-3 py-2 font-mono text-[11px] border-destructive text-destructive`}>{error}</div>}

      {/* ── Filters ─────────────────────────────────────────────── */}
      <div className={`flex flex-wrap items-center gap-2 px-4 py-2 border-b ${border} ${panelBg}`}>
        <button type="button" onClick={() => { setOpenOnly((v) => !v); setStatuses([]) }}
          className={`rounded-[4px] border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${openOnly && statuses.length === 0 ? 'border-signal bg-signal-surface text-signal' : `${border} ${muted}`}`}>open only</button>
        {STATUS_ORDER.map((s) => {
          const active = statuses.includes(s)
          return (
            <button key={s} type="button" onClick={() => setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))}
              className={`rounded-[4px] border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${active ? 'border-signal bg-signal-surface text-signal' : `${border} ${muted} hover:text-signal hover:border-signal`}`}>
              {TASK_STATUS_LABEL[s]}
            </button>
          )
        })}
        <span className={`mx-1 ${muted}`}>·</span>
        <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>planned</span>
        {plannedChannelIds.map(channelChip)}
        <span className={`ml-1 font-mono text-[9px] uppercase tracking-widest ${muted}`}>manual</span>
        {manualChannelIds.map(channelChip)}
        <div className="w-48">
          <SearchableSelect theme={theme} value={platformId === '' ? undefined : platformId} options={catalog.platforms}
            placeholder="All brands" searchPlaceholder="Search platforms…" noneLabel="All brands"
            onChange={(id) => setPlatformId(id ? Number(id) : '')} />
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') refreshAll() }} placeholder="domain…"
          className={`w-40 rounded border px-2 py-0.5 font-mono text-[10px] ${border} bg-card text-foreground`} />
        <span className={`ml-auto font-mono text-[10px] uppercase tracking-widest ${muted}`}>{rows.length} / {list?.total ?? '—'}</span>
        <button type="button" onClick={() => setShowConfig((v) => !v)} className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${showConfig ? 'border-signal text-signal' : `${border} ${muted}`} hover:text-signal hover:border-signal`}>
          <Settings2 className="h-3 w-3" /> Channels
        </button>
      </div>

      {/* ── Channel configuration ───────────────────────────────── */}
      {showConfig && (
        <div className={`px-4 py-3 border-b ${border} ${panelBg}`}>
          <div className={`mb-2 flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest ${muted}`}><Settings2 className="h-3 w-3" /> Channel configuration {configNote && <span className={configNote.startsWith('Saved') || configNote === 'Deleted.' ? 'text-success' : 'text-destructive'}>· {configNote}</span>}</div>
          {!config ? (
            <div className={`font-mono text-[11px] ${muted}`}><Loader2 className="inline h-3 w-3 animate-spin" /> Loading…</div>
          ) : (
            <ChannelConfigPanel theme={theme} config={config} platforms={catalog.platforms} onSaveChannel={saveChannel} onSaveRegistrar={saveRegistrar} onDeleteRegistrar={deleteRegistrar} />
          )}
        </div>
      )}

      {/* ── Actions ─────────────────────────────────────────────── */}
      {/* A plain block, not overflow-auto: a scroll container here (even one that
          never overflows) captured the sticky headers and sat them over the
          newest rows (Jobs page bug, 2026-09-20). The ten-row box and its
          scrolling belong to ResizableTable's maxRows. */}
      <div>
        {loading ? (
          <div className={`flex h-48 items-center justify-center gap-2 font-mono text-[11px] ${muted}`}><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <ResizableTable columns={columns} stickyHeader maxRows={10} theme={theme} isEmpty={rows.length === 0} emptyMessage="No actions match these filters">
            {rows.map((t) => {
              const isOpen = expanded === t.action_id
              return [
                <tr key={t.action_id} data-action-id={t.action_id} onClick={() => setExpanded(isOpen ? null : t.action_id)} className={`cursor-pointer border-b transition-colors border-border hover:bg-secondary`}>
                  <TableTd className={muted}>{isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}</TableTd>
                  <TableTd className={`font-mono ${muted}`}>{t.platform_name_code ?? platformName(t.platform_id) ?? '—'}</TableTd>
                  <TableTd className={`font-mono ${text}`} title={t.subject_label ?? undefined}>{t.subject_label ?? '—'}</TableTd>
                  <TableTd className={`font-mono ${muted}`}>{t.channel}</TableTd>
                  <TableTd><span className="inline-flex items-center gap-1"><Chip label={TASK_STATUS_LABEL[t.status]} tone={TASK_STATUS_TONE[t.status]} />{t.resolution_candidate && t.status !== 'resolved' && <Chip label="taken down?" tone="amber" />}</span></TableTd>
                  <TableTd className={`font-mono truncate ${muted}`} title={t.target ?? undefined}>{t.target ?? t.email_to ?? '—'}</TableTd>
                  <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtHours(t.age_hours)}</TableTd>
                  <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtDate(t.sent_at)}</TableTd>
                  <TableTd className={`font-mono truncate ${muted}`} title={t.last_reply_excerpt ?? undefined}>{t.last_reply_from ? `${t.last_reply_from}: ${t.last_reply_excerpt ?? ''}` : '—'}</TableTd>
                  <TableTd className={`font-mono truncate text-[11px] ${t.status === 'failed' ? ('text-destructive') : muted}`} title={nextAction(t)}>{nextAction(t)}</TableTd>
                </tr>,
                isOpen ? (
                  <tr key={`${t.action_id}-detail`} className={`border-b border-border`}>
                    <td colSpan={columns.length} className="px-4 py-3">
                      {/* A legacy log row (an old manual send) can predate the
                          entry it was about; there is no entry widget to show. */}
                      {t.user_entry_id ? (
                        <ActionsWidget userEntryId={t.user_entry_id} hostname={t.hostname} hasDomain={t.hostname != null} theme={theme} onChanged={() => { fetchSummary(); fetchList(0) }} />
                      ) : (
                        <span className={`font-mono text-[11px] ${muted}`}>not linked to an entry (legacy log)</span>
                      )}
                    </td>
                  </tr>
                ) : null,
              ]
            })}
          </ResizableTable>
        )}
        {hasMore && !loading && (
          <div className="flex justify-center py-3">
            <button type="button" onClick={() => fetchList((list?.page ?? 0) + 1)} className={`rounded border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-signal hover:border-signal`}>Load more</button>
          </div>
        )}
      </div>
    </BaseDashboard>
  )
}

// Per brand × channel: on/off, recipients (the CNCS address), params (form
// URL, reporter identity). platform NULL = the global default a brand row
// overrides. A manual channel has nothing to configure but its on/off — its
// recipients and body are typed on the screen that sends it. Below it, the
// registrar abuse contacts.
function ChannelConfigPanel({ theme, config, platforms, onSaveChannel, onSaveRegistrar, onDeleteRegistrar }: {
  theme: ProcessedTheme
  config: ConfigPayload
  platforms: Array<{ id: number; name_code: string; name?: string }>
  onSaveChannel: (row: { platform_id: number | null; channel: string; enabled?: boolean; recipients?: string[]; params?: Record<string, any> }) => Promise<void>
  onSaveRegistrar: (row: Partial<RegistrarRow>) => Promise<void>
  onDeleteRegistrar: (row: RegistrarRow) => Promise<void>
}) {
  const { isDark, muted, border, text } = baseDashboardClasses(theme)
  const [platformId, setPlatformId] = useState<number | null>(null)
  const [drafts, setDrafts] = useState<Record<string, { recipients: string[]; params: string }>>({})
  const [newRegistrar, setNewRegistrar] = useState<Partial<RegistrarRow>>({ registrar_pattern: '', abuse_email: '', form_url: '', override_whois: false, priority: 100 })
  const inputCls = `rounded border px-2 py-1 font-mono text-[11px] ${border} bg-card text-foreground`

  const rowFor = (channel: string) => config.channels.find((r) => r.channel === channel && (platformId == null ? r.platform_id == null : r.platform_id === platformId))
  const globalFor = (channel: string) => config.channels.find((r) => r.channel === channel && r.platform_id == null)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>Brand</span>
        <div className="w-56">
          <SearchableSelect theme={theme} value={platformId ?? undefined} options={platforms.map((p) => ({ id: p.id, name: p.name ?? p.name_code }))}
            placeholder="Global defaults" searchPlaceholder="Search platforms…" noneLabel="Global defaults"
            onChange={(id) => { setPlatformId(id ? Number(id) : null); setDrafts({}) }} />
        </div>
        <span className={`font-mono text-[10px] ${muted}`}>A brand row overrides the global one for that channel; leave a channel untouched to inherit.</span>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {config.catalog.map((ch) => {
          const row = rowFor(ch.id)
          const inherited = !row && platformId != null ? globalFor(ch.id) : null
          const eff = row ?? inherited
          const draft = drafts[ch.id] ?? { recipients: eff?.recipients ?? [], params: JSON.stringify(eff?.params ?? {}, null, 0) }
          const setDraft = (d: Partial<typeof draft>) => setDrafts((prev) => ({ ...prev, [ch.id]: { ...draft, ...d } }))
          const manual = ch.manual_only || isManualChannel(ch.id)
          return (
            <div key={ch.id} className={`rounded border px-3 py-2 ${border}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`font-mono text-[12px] ${text}`}>{ch.label}</span>
                <Chip label={ch.kind} tone="neutral" />
                {manual && <Chip label="manual" tone="neutral" title="Logged when an operator sends it; never planned" />}
                {inherited && <Chip label="inherited" tone="neutral" title="No brand row; the global default applies" />}
                <span className="flex-1" />
                <label className={`inline-flex items-center gap-1 font-mono text-[10px] ${muted}`}>
                  <input type="checkbox" checked={eff?.enabled ?? true} onChange={(e) => onSaveChannel({ platform_id: platformId, channel: ch.id, enabled: e.target.checked })} /> enabled
                </label>
              </div>
              {manual ? (
                <div className={`mt-2 font-mono text-[10px] ${muted}`}>sent from its own screen</div>
              ) : (
                <>
                  {ch.kind === 'email' && ch.id !== 'registrar_abuse' && (
                    <div className="mt-2">
                      <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Recipients</span>
                      <EmailPillInput value={draft.recipients} onChange={(v) => setDraft({ recipients: v })} theme={theme} placeholder={ch.id === 'cncs' ? 'the brand\'s CNCS / cybersecurity office address' : 'address'} />
                    </div>
                  )}
                  <div className="mt-2">
                    <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Params (JSON){ch.kind === 'browser_form' ? ' · form_url, reporter_name, reporter_email' : ''}</span>
                    <input value={draft.params} onChange={(e) => setDraft({ params: e.target.value })} className={`${inputCls} mt-0.5 w-full`} />
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <button type="button" onClick={() => {
                      let params: Record<string, any> = {}
                      try { params = JSON.parse(draft.params || '{}') } catch { return }
                      onSaveChannel({ platform_id: platformId, channel: ch.id, recipients: draft.recipients, params })
                    }} className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border-success/40 text-success hover:bg-success-surface`}>Save</button>
                    {eff?.updated_by && <span className={`font-mono text-[10px] ${muted}`}>by {eff.updated_by} · {fmtDate(eff.updated_at)}</span>}
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>

      <div className={`mt-2 font-mono text-[9px] uppercase tracking-widest ${muted}`}><ShieldCheck className="inline h-3 w-3" /> Registrar abuse contacts · matched case-insensitively against the WHOIS registrar; WHOIS wins unless "override"</div>
      <div className="flex flex-col gap-1">
        {config.registrars.map((r) => (
          <div key={r.id} className={`flex flex-wrap items-center gap-2 rounded border px-3 py-1.5 ${border}`}>
            <span className={`font-mono text-[11px] ${text}`}>{r.registrar_pattern}</span>
            <span className={`font-mono text-[11px] ${muted}`}>{r.abuse_email ?? '—'}</span>
            {r.form_url && <a href={r.form_url} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-signal hover:underline">form</a>}
            {r.override_whois && <Chip label="override" tone="amber" />}
            <span className={`font-mono text-[10px] ${muted}`}>prio {r.priority}</span>
            {r.notes && <span className={`font-mono text-[10px] ${muted}`}>{r.notes}</span>}
            <span className="flex-1" />
            <button type="button" onClick={() => onSaveRegistrar({ ...r, override_whois: !r.override_whois })} className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-warning hover:border-warning-border`}>{r.override_whois ? 'prefer whois' : 'override whois'}</button>
            <button type="button" onClick={() => onDeleteRegistrar(r)} className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-destructive hover:border-destructive`}>delete</button>
          </div>
        ))}
        <div className={`flex flex-wrap items-center gap-2 rounded border px-3 py-1.5 ${border}`}>
          <input value={newRegistrar.registrar_pattern ?? ''} onChange={(e) => setNewRegistrar({ ...newRegistrar, registrar_pattern: e.target.value })} placeholder="%registrar name%" className={`${inputCls} w-44`} />
          <input value={newRegistrar.abuse_email ?? ''} onChange={(e) => setNewRegistrar({ ...newRegistrar, abuse_email: e.target.value })} placeholder="abuse@…" className={`${inputCls} w-52`} />
          <input value={newRegistrar.form_url ?? ''} onChange={(e) => setNewRegistrar({ ...newRegistrar, form_url: e.target.value })} placeholder="abuse form URL (optional)" className={`${inputCls} w-64`} />
          <label className={`inline-flex items-center gap-1 font-mono text-[10px] ${muted}`}><input type="checkbox" checked={!!newRegistrar.override_whois} onChange={(e) => setNewRegistrar({ ...newRegistrar, override_whois: e.target.checked })} /> override whois</label>
          <button type="button" disabled={!(newRegistrar.registrar_pattern ?? '').trim()} onClick={() => { onSaveRegistrar({ ...newRegistrar, abuse_email: newRegistrar.abuse_email || null, form_url: newRegistrar.form_url || null }); setNewRegistrar({ registrar_pattern: '', abuse_email: '', form_url: '', override_whois: false, priority: 100 }) }}
            className="rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border-success/40 text-success hover:bg-success-surface disabled:opacity-40">Add</button>
        </div>
      </div>
    </div>
  )
}
